import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, logInfo } from '@theme/utilities';

/**
 * DEBUG — "did the customer just come back from checkout?" detection.
 *
 * Nothing on the returning page says where we came from. `document.referrer` is
 * restored from the session history entry, so after a back navigation it still reports
 * whoever referred us the *first* time this document loaded — not checkout. So we leave
 * a marker on the way out and read it on the way back in.
 *
 * Remove alongside the cart debug toolbar in `snippets/cart-products.liquid`.
 */
const CHECKOUT_DEPARTURE_KEY = 'cart:left-for-checkout';

/**
 * How long a departure marker is worth acting on.
 *
 * A checkout click that never navigated — a failed request, a dismissed wallet sheet —
 * leaves a marker armed. Without a bound it would be spent on whatever back navigation
 * happened next, which could be an unrelated one much later in the session.
 */
const CHECKOUT_DEPARTURE_TTL_MS = 60 * 60 * 1000;

/**
 * Everything that can send this tab to checkout, minus links. The accelerated buttons
 * render inside a shadow root, but shadow-DOM clicks retarget to the host element before
 * they reach `document`, so matching the host is enough.
 */
const CHECKOUT_TRIGGER_SELECTOR = [
  '[name="checkout"]',
  'shopify-accelerated-checkout',
  'shopify-accelerated-checkout-cart',
  '[data-shopify="dynamic-checkout-cart"]',
].join(', ');

/**
 * `checkout` or `checkouts` as a whole path segment, so a locale prefix still matches
 * (`/fr/checkout`) but a page that merely starts with the word does not
 * (`/pages/checkout-help`, `/blogs/news/checkout-tips`).
 */
const CHECKOUT_PATHNAME = /(^|\/)checkouts?(\/|$)/;

/**
 * True when the click landed inside a link that really resolves to checkout.
 *
 * Matching `[href*="/checkout"]` in the selector instead would be a substring test rather
 * than a path test, and would fire on any URL with a segment merely beginning "checkout".
 * `HTMLAnchorElement.href` is already resolved against the document, so relative hrefs
 * work; it falls back to the raw attribute for values `URL` cannot parse, hence the catch.
 *
 * @param {Element} element
 * @returns {boolean}
 */
const isCheckoutLink = (element) => {
  const link = element.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return false;

  try {
    return CHECKOUT_PATHNAME.test(new URL(link.href).pathname);
  } catch {
    return false;
  }
};

/**
 * The departure marker is best-effort, so every access to it is guarded.
 *
 * `sessionStorage` is not only fallible on write (a full quota rejects it): in a
 * storage-blocked or partitioned context the property getter itself throws, which would
 * otherwise take out the `pageshow` handler on *every* page load rather than only on
 * checkout clicks. Losing the marker costs a refresh we would have liked to run — never a
 * checkout the shopper was trying to reach.
 *
 * Each failure leaves a `logInfo` breadcrumb rather than passing silently. This feature's
 * only symptom when it fails is that nothing happens — which is indistinguishable from the
 * staleness it exists to fix — so a swallowed error here would be untraceable without a
 * rebuild. Switch LOGS_ENABLED on to see them.
 */
const readDeparture = () => {
  try {
    return sessionStorage.getItem(CHECKOUT_DEPARTURE_KEY);
  } catch (error) {
    logInfo('[cart] departure marker unreadable', error);
    return null;
  }
};

const writeDeparture = () => {
  try {
    sessionStorage.setItem(CHECKOUT_DEPARTURE_KEY, String(Date.now()));
  } catch (error) {
    // Storage unavailable or full. With no marker to read back, the refresh never fires.
    logInfo('[cart] departure marker not written', error);
  }
};

const clearDeparture = () => {
  try {
    sessionStorage.removeItem(CHECKOUT_DEPARTURE_KEY);
  } catch (error) {
    // If we could not write a marker, there is nothing to clear.
    logInfo('[cart] departure marker not cleared', error);
  }
};

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(CHECKOUT_TRIGGER_SELECTOR) && !isCheckoutLink(target)) return;

    writeDeparture();
  },
  // Capture, so we still record the departure if something downstream stops propagation.
  { capture: true }
);

window.addEventListener('pageshow', async (event) => {
  // `PerformanceNavigationTiming.type` describes how *this document* was fetched. A bfcache
  // restore never re-fetches it, so the entry still reports the original navigation and
  // `event.persisted` is the only signal we get. When bfcache is missed the page really is
  // re-fetched and the entry flips to `back_forward`. We need both to cover both paths.
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  const departedAt = readDeparture();

  if (!event.persisted && navigationType !== 'back_forward') {
    // Forward navigation. Any marker left over is from a completed checkout (post-purchase
    // "continue shopping") or an abandoned one — drop it so it can't fire on a later back.
    clearDeparture();
    return;
  }

  if (!departedAt) return;

  clearDeparture();

  // Written as a range so a NaN marker (garbage in storage) and a negative age (the clock
  // moved backwards between the two reads) both fall through to the discard.
  const awayForMs = Date.now() - Number(departedAt);
  if (!(awayForMs >= 0 && awayForMs <= CHECKOUT_DEPARTURE_TTL_MS)) {
    logInfo('[cart] discarded stale checkout marker', { awayForMs });
    return;
  }

  logInfo('[cart] returned from checkout', {
    restoredFromBfcache: event.persisted,
    navigationType,
    awayForMs,
    drawerOpen: Boolean(document.querySelector('cart-drawer-component dialog[open]')),
  });

  // A bfcache restore re-runs nothing, so the discounts on screen are whatever the server
  // said before checkout re-evaluated them. Re-render the cart sections from the server to
  // pick up whatever checkout decided. `refreshFromServer` fans out across every mounted
  // cart section, so the first instance covers the drawer and the /cart page alike.
  const [cartItems] = document.querySelectorAll('cart-items-component');
  if (typeof cartItems?.refreshFromServer !== 'function') return;

  try {
    const results = await cartItems.refreshFromServer();
    const failed = results.filter((result) => result.status === 'rejected');

    for (const failure of failed) console.warn('[cart] checkout-return refresh failed', failure.reason);

    logInfo(`[cart] checkout-return refresh — ${results.length - failed.length}/${results.length} section(s)`);
  } catch (error) {
    // `refreshFromServer` is not async: it reads `sectionId` synchronously, and that getter
    // throws when `data-section-id` is missing, so the throw escapes before there is a
    // promise to carry it. Inside this async listener it would surface as an unhandled
    // rejection, with the marker already consumed and nothing left to retry from.
    //
    // `error` rather than `warn`, and deliberately louder than the per-section failures
    // above: nothing else can reach here. `Promise.allSettled` never rejects and the
    // filtering and logging cannot throw, so the only arrival is that missing attribute —
    // a markup defect with no environmental cause, and nothing a shopper can provoke.
    // The per-section loop stays at `warn` precisely because it cannot make that claim:
    // a flaky network and a broken section produce the same rejection there.
    console.error('[cart] checkout-return refresh threw', error);
  }
});

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#openFromQueryString();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;

    if (event.detail?.data?.didError) {
      return;
    }

    if (!this.hasAttribute('auto-open')) return;

    if (event.detail?.data?.source === 'discount-kit-sync') return;

    // Defer the open so the cart-items-component morph (which is triggered
    // by the same CartAddEvent and runs synchronously in its listener) and
    // every component's updatedCallback (queued as microtasks by morph.js)
    // all finish first. Without this, the dialog can open with stale
    // content and visibly snaps to the updated cart a moment later —
    // a flash for sighted users and, more importantly, a screen-reader
    // announcement that describes a cart state that no longer exists.
    // Doing this made the behavior of the screen reader more consistent
    // on every open.
    requestAnimationFrame(() => this.showDialog());
  };

  #openFromQueryString = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') !== 'open') return;

    url.searchParams.delete('cart');
    history.replaceState(history.state, '', url);
    requestAnimationFrame(() => this.open());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
